import { useQuery } from '@apollo/client'
import AppBar from '@material-ui/core/AppBar'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { useTheme } from '@material-ui/core/styles'
import { useMemo } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import { ACCOMMODATION_NAME_QUERY } from '../../graphql/accommodation'
import { ROOMTYPE_NAME_QUERY } from '../../graphql/roomtype'
import { TabPanel } from '../../helpers/TabPanel'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import roomTypePagesRoleManagement from '../../staticRoleManagement/roomTypeRoleManagement'
import { UploadBoxForRoomType } from '../RoomTypeUpload'
import RoomTypesFacilities from '../RoomTypesFacilities'
import RoomtypeForm from '../RoomtypeForm'
import useStyles from './styles/index.styles'

export default function RoomtypeTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const { roomTypeId, roomTypeCode, page, accommodationID, propertyCode } =
    useParams<{
      roomTypeId: string
      roomTypeCode: string
      page: string
      accommodationID: string
      propertyCode: string
    }>()

  const { data: hotelData, loading: roomtypeLoading } = useQuery<{
    roomTypeName: {
      name: string
    }
  }>(ROOMTYPE_NAME_QUERY, {
    variables: {
      propertyCode: propertyCode,
      roomTypeCode: roomTypeCode
    }
  })

  const { data: accommodationNameData, loading: accLoading } = useQuery<{
    accommodationName: {
      name: string
    }
  }>(ACCOMMODATION_NAME_QUERY, {
    variables: {
      propertyCode: propertyCode
    }
  })
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Accommodations'), link: '/accommodations' },
      {
        label: accommodationNameData?.accommodationName.name || '',
        link: `/accommodations/${accommodationID}/${propertyCode}/details`
      },
      {
        label: getTranslate('room types'),
        link: `/accommodations/${accommodationID}/${propertyCode}/room-types/list`
      },
      {
        label: hotelData?.roomTypeName.name || ''
      }
    ],
    [accommodationNameData, hotelData]
  )

  const tabsList = [
    {
      value: 'details',
      className: classes.tab,
      label: getTranslate('Details'),
      component: LocalizedLink,
      to: `/accommodations/${accommodationID}/${propertyCode}/room-types/${roomTypeId}/${roomTypeCode}/details`
    },
    {
      value: 'images',
      className: classes.tab,
      label: getTranslate('Images'),
      component: LocalizedLink,
      to: `/accommodations/${accommodationID}/${propertyCode}/room-types/${roomTypeId}/${roomTypeCode}/images`
    },
    {
      value: 'facilities',
      className: classes.tab,
      label: getTranslate('Facilities'),
      component: LocalizedLink,
      to: `/accommodations/${accommodationID}/${propertyCode}/room-types/${roomTypeId}/${roomTypeCode}/facilities`
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = roomTypePagesRoleManagement.find(
    (item) =>
      item.route ===
      '/accommodations/:accommodationID/:propertyCode/room-types/:roomTypeId/:roomTypeCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  return (
    <div className={classes.root}>
      <AppBar position="static" color="transparent">
        <Breadcrumbs
          levels={cachedLevels}
          loading={accLoading || roomtypeLoading}
        />
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth">
          {visibleTabs.map((item, index) => (
            <Tab
              key={index}
              value={item.value}
              className={item.className}
              label={item.label}
              component={item.component}
              to={item.to}
            />
          ))}
        </Tabs>
      </AppBar>
      <TabPanel
        value={page}
        index="details"
        dir={theme.direction}
        className={classes.tabPanel}>
        <RoomtypeForm />
      </TabPanel>
      <TabPanel
        value={page}
        index="images"
        dir={theme.direction}
        className={classes.tabPanel}>
        <UploadBoxForRoomType />
      </TabPanel>
      <TabPanel
        value={page}
        index="facilities"
        dir={theme.direction}
        className={classes.tabPanel}>
        <RoomTypesFacilities />
      </TabPanel>
    </div>
  )
}
